import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdDevices, MdOutlineSecurity, MdOutlineLocalShipping, MdOutlineVerified, MdAutoAwesome, MdLogin } from 'react-icons/md';

export default function PixelLayout({ p }) {
  const T = p.storeTheme || {};
  const buttonColor = T.buttonColor || "#1a73e8"; // Google Blue
  const secondaryColor = T.secondaryColor || "#34a853"; // Google Green

  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Designed by Google. Built with AI.",
    title: "Pixel Pro. Pure Magic.",
    subtitle: "Discover the phone that puts Google AI at the center of your world. Engineered with Tensor G5 and pro-grade cameras.",
    primaryCta: "Explore Pixel",
    secondaryCta: "Store Info",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f9fa] text-[#3c4043] antialiased" style={{ fontFamily: T.fontFamily || "Outfit, sans-serif" }}>
      {/* Top Google-style banner */}
      <div className="w-full bg-[#f1f3f4] border-b border-gray-200/80 py-2.5 px-4 text-center text-xs font-medium text-[#5f6368]">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <MdAutoAwesome className="text-[#1a73e8] animate-pulse" /> Try Gemini Live free for the first 12 months with selected Pixel devices.
        </span>
      </div>

      {/* Google-style Clean Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur shadow-md border-b border-gray-150" 
          : "bg-[#f8f9fa]/90 backdrop-blur-sm border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-gray-100 shadow-sm" size="h-9 w-9" />
            <div>
              <p className="text-base font-bold tracking-tight text-[#202124]">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#5f6368] font-bold tracking-widest uppercase">{p.storeInfo?.businessCategory || "Google Tech Partner"}</p>
            </div>
          </div>

          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Explore Store"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-bold tracking-wide transition-all ${
                  activePage === page 
                    ? "text-[#1a73e8]" 
                    : "text-[#5f6368] hover:text-[#202124]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />

            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-gray-100 text-[#5f6368] transition-colors" title="Wishlist">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#ea4335] shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />

            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-gray-150 text-[#202124]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Explore Store"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-sm font-semibold border-b border-gray-100 ${
                  activePage === page ? "text-[#1a73e8] font-bold" : "text-[#5f6368]"
                }`}
              >
                {label}
              </button>
            ))}

            {/* Login Options for Mobile */}
            {!p.userProfile ? (
              <button
                onClick={() => {
                  p.handleOpenLogin();
                  setMobileMenuOpen(false);
                }}
                className="py-2 text-left text-sm font-semibold border-b border-gray-100 flex items-center gap-2 text-[#1a73e8]"
              >
                <MdLogin className="text-lg" />
                Login / Sign Up
              </button>
            ) : (
              <div className="py-2 border-b border-gray-100 flex flex-col gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#1a73e8] flex items-center justify-center text-xs font-semibold text-white shadow-sm">
                    {p.userProfile?.name ? p.userProfile.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-[#202124]">{p.userProfile?.name}</p>
                    <p className="text-[10px] text-[#5f6368]">{p.userProfile?.customer_mobile}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      p.handleViewProfile();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-bold text-[#1a73e8] bg-[#e8f0fe] hover:bg-[#d2e3fc] px-3 py-1.5 rounded-full transition-colors"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      p.handleViewOrders();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-bold text-[#1a73e8] bg-[#e8f0fe] hover:bg-[#d2e3fc] px-3 py-1.5 rounded-full transition-colors"
                  >
                    Orders
                  </button>
                  <button
                    onClick={() => {
                      p.handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-bold text-[#ea4335] bg-[#fce8e6] hover:bg-[#fad2cf] px-3 py-1.5 rounded-full transition-colors ml-auto"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Google Split Hero Header */}
            <section className="w-full max-w-6xl mx-auto px-6 py-12 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
              <div className="flex flex-col items-start pr-0 md:pr-6">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#e8f0fe] text-xs font-semibold text-[#1a73e8] mb-4">
                  <MdAutoAwesome className="text-xs" />
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#202124] leading-tight mb-4">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-base md:text-lg text-[#5f6368] font-normal leading-relaxed mb-8">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="flex gap-4">
                  <button 
                    onClick={() => navigateTo("shop")}
                    className="px-6 py-3 rounded-full text-xs font-bold text-white hover:opacity-90 transition-all shadow-sm"
                    style={{ backgroundColor: buttonColor }}
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                  <button 
                    onClick={() => {
                      p.setActiveTab("details");
                      navigateTo("about");
                    }}
                    className="px-6 py-3 rounded-full text-xs font-bold border border-gray-300 hover:bg-gray-50 text-[#3c4043] transition-all"
                  >
                    <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                  </button>
                </div>
              </div>

              {/* Pixel Hero Visual */}
              <div className="w-full rounded-[2.5rem] overflow-hidden bg-white border border-gray-200/60 shadow-[0_20px_40px_rgba(0,0,0,0.03)] p-4">
                <HeroImage p={p} themeId="pixel" className="h-[320px] md:h-[450px] rounded-[2rem] overflow-hidden" />
              </div>
            </section>

            {/* Google Tech Spotlight */}
            <section className="bg-white border-y border-gray-200/60 py-16">
              <div className="max-w-6xl mx-auto px-6">
                <div className="text-center mb-12">
                  <span className="text-xs uppercase tracking-[0.15em] font-extrabold text-[#1a73e8]">Google Engineering</span>
                  <h2 className="text-3xl font-bold tracking-tight text-[#202124] mt-1.5">Intelligent Inside and Out</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                  {[
                    {
                      title: "Tensor G5 Co-Silicon",
                      desc: "Custom-built chip designed for advanced on-device AI, high-speed photography, and maximum efficiency.",
                      badge: "3nm Core",
                      badgeColor: "bg-[#e8f0fe] text-[#1a73e8]"
                    },
                    {
                      title: "Gemini Advanced Core",
                      desc: "Magic Eraser, Real Tone, Best Take, and live audio translation powered by Google's top-tier language models.",
                      badge: "AI Powered",
                      badgeColor: "bg-[#e6f4ea] text-[#137333]"
                    },
                    {
                      title: "Actua LTPO OLED",
                      desc: "Super-bright OLED screen reaching up to 3000 nits peak brightness with adaptive 120Hz smooth scrolling.",
                      badge: "3000 nits",
                      badgeColor: "bg-[#fef7e0] text-[#b06000]"
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-[#f8f9fa] border border-gray-150 rounded-[2rem] p-8 flex flex-col justify-between hover:shadow-md transition-shadow">
                      <div>
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 ${item.badgeColor}`}>
                          {item.badge}
                        </span>
                        <h4 className="text-lg font-bold text-[#202124]">{item.title}</h4>
                        <p className="text-xs text-[#5f6368] mt-2.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Badges / Support */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "7 Years Updates", icon: <MdOutlineSecurity className="text-2xl text-[#1a73e8]" />, desc: "Long-term security commits" },
                  { title: "Recycled Build", icon: <MdOutlineVerified className="text-2xl text-[#34a853]" />, desc: "Eco-friendly aluminum chassis" },
                  { title: "Safe Store Shipping", icon: <MdOutlineLocalShipping className="text-2xl text-[#f9ab00]" />, desc: "Fast & insured delivery" },
                  { title: "Pixel Support", icon: <MdDevices className="text-2xl text-[#ea4335]" />, desc: "Instant technician support" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 bg-white border border-gray-200/80 rounded-2xl shadow-sm">
                    <div className="p-3 rounded-full bg-[#f8f9fa] mb-3">{b.icon}</div>
                    <p className="text-xs font-bold text-[#202124]">{b.title}</p>
                    <p className="text-[10px] text-[#5f6368] mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#1a73e8]">STORE CATALOG</span>
              <h3 className="text-3xl font-bold text-[#202124] mt-1">Explore Products</h3>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-xs font-bold text-[#3c4043] transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-full border border-gray-200 bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff" } : { color: "#5f6368" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
